import { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Autocomplete,
  Box,
  Typography,
  IconButton,
  DialogActions,
  Divider,
  Skeleton,
  Chip,
  InputAdornment,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ingressService from "../../../services/ingressService";
import productsService from "../../../services/productsService";
import CircularProgress from "@mui/material/CircularProgress";

const initialAlert = {
  severity: "success",
  message: "",
};

const CrearProductoCombo = ({
  handleCreateProducto,
  productSKU,
  onClose,
  secondaryLabel = "Cancelar",
  onSecondary,
  showBackIcon = false,
}) => {
  const [sku, setSku] = useState("");
  const [nombre, setNombre] = useState("");
  const [productos, setProductos] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedProducto, setSelectedProducto] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [textEntered, setTextEntered] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const user_id = sessionStorage.getItem("User");
  const [alertObj, setAlertObj] = useState(initialAlert);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [errors, setErrors] = useState({ sku: "", nombre: "" });

  useEffect(() => {
    if (productSKU) {
      setLoadingSpinner(true);
      const fetchProductCombo = async () => {
        try {
          const res = await productsService.getProductsBySku(productSKU);
          const productData = Array.isArray(res.data) ? res.data[0] : res.data;
          setSku(productData.sku_id);
          setNombre(productData.name);
          const productDetails = await Promise.all(
            productData.combo.map(async (comboItem) => {
              const productRes = await productsService.getProductsBySku(
                comboItem.product_id
              );
              return {
                id: comboItem.product_id,
                cantidad: comboItem.quantity,
                name: productRes.data[0].name,
                sku_id: productRes.data[0].sku_id,
              };
            })
          );

          setProductos(productDetails);
        } catch (error) {
          console.error("Error al cargar los datos del combo:", error);
        } finally {
          setLoadingSpinner(false);
        }
      };

      fetchProductCombo();
    }
  }, [productSKU]);

  const clearForm = () => {
    onClose();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (textEntered.length >= 3) {
      filterProductsByText(textEntered);
    } else {
      setFilteredProducts([]);
    }
  }, [textEntered]);

  const filterProductsByText = async (text) => {
    setIsSearching(true);
    try {
      // El backend ya filtra por SKU/EAN/nombre (description). Usamos sus resultados
      // tal cual; el Autocomplete NO debe re-filtrar (ver filterOptions abajo).
      const res = await ingressService.getInventory(text, true);
      setFilteredProducts(res?.data?.results || []);
    } catch (error) {
      console.error("Error al filtrar productos:", error);
      setFilteredProducts([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddProducto = () => {
    if (selectedProducto) {
      const exists = productos.find((p) => p.id === selectedProducto.id);
      if (!exists) {
        setProductos([...productos, { ...selectedProducto, cantidad: 1 }]);
        setSelectedProducto(null);
        setSearch("");
        setTextEntered("");
        setFilteredProducts([]);
      } else {
        setAlertObj({
          severity: "warning",
          message: "El producto ya esta en la lista.",
        });
        setSnackbarOpen(true);
      }
    }
  };

  const handleRemoveProduct = (index) => {
    const updatedProductos = [...productos];
    updatedProductos.splice(index, 1);
    setProductos(updatedProductos);
  };

  const handleCrear = async () => {
    let newErrors = { sku: "", nombre: "" };
    let valid = true;
    if (!sku.trim()) {
      newErrors.sku = "El SKU es obligatorio";
      valid = false;
    }
    if (!nombre.trim()) {
      newErrors.nombre = "El nombre es obligatorio";
      valid = false;
    }
    setErrors(newErrors);
    if (!valid) return;

    setLoadingButton(true);
    const productoCombo = {
      sku_id: sku,
      ean_id: `EAN${sku}`,
      name: nombre,
      height: 1,
      deep: 1,
      width: 1,
      weight: 1,
      observation: "observation combo test",
      user_id: Number(user_id),
      is_combo: true,
      active: true,
      combo: productos.map((producto) => ({
        product_id: producto.id,
        quantity: Number(producto.cantidad),
      })),
    };

    try {
      if (productSKU) {
        productoCombo.id = productSKU;

        await productsService.updateCombo(productoCombo);
        setAlertObj({
          severity: "success",
          message: "Combo editado exitosamente.",
        });
      } else {
        await productsService.postProductCombo(productoCombo);
        setAlertObj({
          severity: "success",
          message: "Combo creado exitosamente.",
        });
      }
      setTimeout(() => {
        handleCreateProducto();
      }, 1500);
    } catch (error) {
      console.error("Error al guardar combo:", error);
      setAlertObj({
        severity: "error",
        message: "Hubo un error al intentar guardar el combo.",
      });
    } finally {
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
      }, 2000);
      setLoadingButton(false);
    }
  };

  const isCrearDisabled = !sku || !nombre || productos.length === 0;

  if (loadingSpinner) {
    return (
      <Box sx={{ py: 2 }}>
        <Grid container spacing={2}>
          {[...Array(2)].map((_, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Skeleton variant="rounded" height={56} />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Skeleton variant="rounded" height={56} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rounded" height={120} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 1 }}>
      {/* Seccion: Identificacion */}
      <Typography
        variant="overline"
        color="text.secondary"
        fontWeight={600}
        sx={{ display: "block", mb: 1.5 }}
      >
        Identificacion del combo
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="SKU"
            placeholder="Ej: COMBO-001"
            variant="outlined"
            size="small"
            fullWidth
            required
            value={sku}
            onChange={(e) => {
              const inputValue = e.target.value.toUpperCase();
              setSku(inputValue);
              setErrors({ ...errors, sku: "" });
            }}
            disabled={!!productSKU}
            error={Boolean(errors.sku)}
            helperText={errors.sku}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Nombre del combo"
            placeholder="Ej: FERNET CON COCA"
            variant="outlined"
            size="small"
            fullWidth
            required
            value={nombre}
            onChange={(e) => {
              setNombre(e.target.value.toUpperCase());
              setErrors({ ...errors, nombre: "" });
            }}
            error={Boolean(errors.nombre)}
            helperText={errors.nombre}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
        </Grid>
      </Grid>

      {/* Seccion: Productos del combo */}
      <Divider sx={{ my: 2.5 }} />
      <Typography
        variant="overline"
        color="text.secondary"
        fontWeight={600}
        sx={{ display: "block", mb: 1.5 }}
      >
        Productos del combo
      </Typography>

      <Grid container spacing={1.5} alignItems="flex-start">
        <Grid item xs>
          <Autocomplete
            options={filteredProducts}
            // No re-filtrar en el cliente: el backend ya matcheó por SKU/EAN/nombre.
            // Sin esto, MUI filtra las opciones contra getOptionLabel (el nombre) y
            // descarta las búsquedas por SKU (ej: "maci" no está en "MACETA...").
            filterOptions={(options) => options}
            getOptionLabel={(option) => (option ? option.name || "" : "")}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={(props, option) => (
              <li {...props}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    SKU: {option.sku_id}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar productos"
                placeholder="Escribi al menos 3 caracteres..."
                variant="outlined"
                size="small"
                onChange={(e) => setTextEntered(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            )}
            noOptionsText={
              isSearching
                ? "Buscando..."
                : textEntered.length < 3
                  ? "Escribi al menos 3 caracteres"
                  : "No se encontraron productos"
            }
            onChange={(event, newValue) => setSelectedProducto(newValue)}
            value={selectedProducto}
          />
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddProducto}
            disabled={!selectedProducto}
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
              height: 40,
              px: 2.5,
            }}
          >
            Agregar
          </Button>
        </Grid>
      </Grid>

      {/* Lista de productos */}
      <Box
        sx={{
          mt: 2,
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 2,
          overflow: "hidden",
          minHeight: 120,
        }}
      >
        {productos.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Producto
                  </TableCell>
                  <TableCell
                    align="center"
                    width={120}
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Cantidad
                  </TableCell>
                  <TableCell align="center" width={60} />
                </TableRow>
              </TableHead>
              <TableBody>
                {productos.map((producto, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "&:hover": { bgcolor: "grey.50" },
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {producto.name}
                        </Typography>
                        {producto.sku_id && (
                          <Typography variant="caption" color="text.secondary">
                            SKU: {producto.sku_id}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="text"
                        value={producto.cantidad}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            const updatedProductos = [...productos];
                            updatedProductos[index].cantidad = value;
                            setProductos(updatedProductos);
                          }
                        }}
                        size="small"
                        inputProps={{
                          style: { textAlign: "center" },
                          inputMode: "numeric",
                        }}
                        sx={{
                          width: 80,
                          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveProduct(index)}
                        sx={{
                          color: "text.secondary",
                          "&:hover": { color: "error.main", bgcolor: "error.50" },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
              px: 2,
            }}
          >
            <LayersOutlinedIcon
              sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              No hay productos agregados
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Busca y agrega productos para armar el combo
            </Typography>
          </Box>
        )}
      </Box>

      {productos.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Chip
            label={`${productos.length} producto${productos.length !== 1 ? "s" : ""} en el combo`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        </Box>
      )}

      {/* Footer actions */}
      <DialogActions sx={{ px: 0, pt: 2.5, pb: 0, justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          onClick={onSecondary || clearForm}
          disabled={loadingButton}
          startIcon={showBackIcon ? <ArrowBackIcon /> : null}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            borderColor: "#cbd5e1",
            color: "#475569",
            "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" },
          }}
        >
          {secondaryLabel}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCrear}
          disabled={isCrearDisabled || loadingButton}
          sx={{
            textTransform: "none",
            px: 3,
            borderRadius: "8px",
            fontWeight: 600,
          }}
        >
          {loadingButton ? (
            <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
          ) : null}
          {loadingButton
            ? productSKU
              ? "Guardando..."
              : "Creando..."
            : productSKU
              ? "Guardar cambios"
              : "Crear combo"}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={alertObj.severity}
          sx={{ width: "100%" }}
        >
          {alertObj.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CrearProductoCombo;
